/**
 * Device Authorization (doractl device-flow login) consent page.
 *
 * doractl (RFC 8628 device flow) prints a short user_code + this page URL.
 * The user opens `/#/device` (optionally `/#/device?user_code=WDJB-MJHT`),
 * enters the user_code, and approves. This page reuses the admin's existing
 * Supabase session and POSTs the live session to
 * POST /v1/auth/device/approve. Unlike CliAuthPage's loopback flow, there is
 * NO redirect and NO callback — the CLI polls in the background and picks up
 * the token itself. This page only shows a "return to your terminal" state.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { baseApiClient, HttpError } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useI18n } from '@/i18n';

/** Normalize user codes: strip whitespace/dashes, uppercase. */
function normalizeUserCode(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase();
}

/** Read the optional `user_code` from the hash query (#/device?user_code=...). */
function readUserCodeFromHash(): string {
  const hash = window.location.hash;
  const queryIndex = hash.indexOf('?');
  const search = queryIndex >= 0 ? hash.slice(queryIndex + 1) : '';
  const query = new URLSearchParams(search);
  return query.get('user_code') || '';
}

type Status = 'idle' | 'approving' | 'approved' | 'cancelled';

export const DeviceAuthPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const initialUserCode = useMemo(() => readUserCodeFromHash(), []);

  const [userCode, setUserCode] = useState(initialUserCode);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);

  // Check Supabase session on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = data.session;
      setIsLoggedIn(!!session);
      setAccount(session?.user?.email || localStorage.getItem('admin_email'));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goToLogin = () => {
    // Preserve the full device hash (with query) so login returns here.
    const returnTo = window.location.hash.slice(1) || '/device';
    sessionStorage.setItem('admin_redirect_after_login', returnTo);
    navigate('/login', { replace: true });
  };

  const handleApprove = async () => {
    setError(null);

    const normalized = normalizeUserCode(userCode);
    if (!normalized) {
      setError(t('deviceAuth.errorEmptyCode'));
      return;
    }

    setStatus('approving');
    try {
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (!session) {
        setIsLoggedIn(false);
        setStatus('idle');
        return;
      }

      // Backend mints a fresh, independent CLI session from the authenticated
      // caller (baseApiClient sends the Bearer) — we no longer hand it a copy
      // of this browser's own Supabase tokens.
      await baseApiClient.post<{ status: string }>('/v1/auth/device/approve', {
        user_code: normalized,
      });

      setStatus('approved');
    } catch (err) {
      const httpErr = err as HttpError;
      const message =
        httpErr?.statusCode === 404
          ? t('deviceAuth.errorUnknownCode')
          : httpErr?.message || t('deviceAuth.errorApprove');
      setError(message);
      setStatus('idle');
    }
  };

  const handleCancel = () => {
    setError(null);
    setStatus('cancelled');
  };

  const renderShell = (body: React.ReactNode) => (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">
            {t('deviceAuth.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('deviceAuth.subtitle')}
          </p>
        </div>
        {body}
      </div>
    </div>
  );

  if (status === 'approved') {
    return renderShell(
      <Card className="p-8 space-y-4 text-center">
        <h2 className="text-lg font-semibold text-success">
          {t('deviceAuth.approvedTitle')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('deviceAuth.approvedDescription')}
        </p>
      </Card>
    );
  }

  if (status === 'cancelled') {
    return renderShell(
      <Card className="p-8 space-y-4 text-center">
        <h2 className="text-lg font-semibold text-foreground">
          {t('deviceAuth.cancelledTitle')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('deviceAuth.cancelledDescription')}
        </p>
      </Card>
    );
  }

  return renderShell(
    <Card className="p-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          {t('deviceAuth.consentHeading')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('deviceAuth.consentDescription')}
        </p>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">
            {t('deviceAuth.accountLabel')}
          </span>
          <span className="font-medium text-foreground text-right break-all">
            {account || '—'}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">
            {t('deviceAuth.scopeLabel')}
          </span>
          <span className="font-medium text-foreground text-right">
            {t('deviceAuth.scopeValue')}
          </span>
        </div>
      </div>

      {isLoggedIn === false ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-warning/50 bg-warning/10 px-4 py-3 text-sm text-warning-foreground">
            {t('deviceAuth.loginRequired')}
          </div>
          <Button variant="primary" className="w-full" onClick={goToLogin}>
            {t('deviceAuth.goToLogin')}
          </Button>
        </div>
      ) : (
        <>
          <Input
            label={t('deviceAuth.userCodeLabel')}
            value={userCode}
            onChange={(e) => setUserCode(e.target.value)}
            placeholder={t('deviceAuth.userCodePlaceholder')}
            autoFocus
            disabled={status === 'approving'}
            className="font-mono tracking-widest text-center uppercase"
          />

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={handleCancel}
              disabled={status === 'approving'}
            >
              {t('deviceAuth.cancel')}
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleApprove}
              loading={status === 'approving'}
              disabled={isLoggedIn === null || status === 'approving'}
            >
              {status === 'approving'
                ? t('deviceAuth.approving')
                : t('deviceAuth.approve')}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default DeviceAuthPage;
